import React, { useState, useEffect, Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Menu, MenuItem } from '@material-ui/core';
import i18n from 'i18n-js';

import { useStyles, customCardStyles } from './TasksDetails.styled';
import clsx from 'clsx';
import moment from 'moment';
import Status from 'components/Status';
import { priorities } from 'constants';
import Loading from 'components/Loading';
import { Card, Tag } from 'components/UI';
import Priority from 'components/Priority';
import TaskConstants from 'constants/tasks';
import Avatar from '@material-ui/core/Avatar';
import SvgIcon from '@material-ui/core/SvgIcon';
import TodayIcon from '@material-ui/icons/Today';
import { TasksActions } from 'state/modules/tasks';
import { forceTimezone, getDynamicLink } from 'utils';
import { Box, Button, Typography } from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';

const TaskModal = React.lazy(() => import('components/Modals/Tasks/CreateEditTask'));

const InfoGrid = ({ classes, title, description, startIcon, startIconText, avatarClassName }) => {
  return (
    <Box display="flex" alignItems="center" style={{ marginBottom: 20 }}>
      <Box className={classes.infoTitle}>
        <Typography variant="h4">
          <Box fontSize={13} fontWeight="fontWeightBold" className={classes.textSecondary}>
            {title}
          </Box>
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" className={classes.infoContent}>
        {startIcon && (
          <>
            {startIcon === 'avatar' ? (
              <Box>
                <Avatar className={clsx(classes.avatar, classes[avatarClassName])}>
                  {startIconText}
                </Avatar>
              </Box>
            ) : (
              <SvgIcon className={classes.icon} component={startIcon} />
            )}
          </>
        )}
        <Typography variant="h4">
          <Box fontSize={13} className={classes.textSecondary}>
            {description}
          </Box>
        </Typography>
      </Box>
    </Box>
  );
};

const TaskWidget = ({ id }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const cardClasses = customCardStyles();
  const [anchorEl, setAnchorEl] = useState({});
  const [openTaskModal, setOpenTaskModal] = useState(false);
  const { localeFormat } = useSelector((state) => state.organizations);

  const { current, loading } = useSelector((state) => state.tasks || {});

  const handleOpenTaskModal = () => setOpenTaskModal((prevValue) => !prevValue);

  /**
   * Open menu
   * @param {*} event
   * @param {*} el
   */
  const handleClick = (event, el) => {
    setAnchorEl({
      ...anchorEl,
      [el]: event.currentTarget,
    });
  };

  /**
   * Close Menu
   */
  const handleClose = (el, payload) => {
    if (payload) {
      updateTask(payload);
    }
    setAnchorEl({
      ...anchorEl,
      [el]: null,
    });
  };

  /**
   * Get task by id
   * @param {object} params
   */
  const getTaskById = () => {
    dispatch(TasksActions.fetchTaskById(id));
  };

  /**
   * Update task
   * @param {object} params
   */
  const updateTask = (payload) => {
    dispatch(TasksActions.fetchUpdateTask(id, payload));
  };

  /**
   * Card title component
   */
  const renderCardHeader = (
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Typography variant="h3">
        <Box fontSize={15} fontWeight="500">
          {i18n.t('tasks.single') + ` - T${id}`}
        </Box>
      </Typography>
      {current?.status && (
        <>
          <Button
            aria-haspopup="true"
            aria-controls="status-menu"
            style={{ textTransform: 'capitalize' }}
            onClick={(e) => handleClick(e, 'status')}
          >
            <Status status={current.status} statusKey="tasks_statuses" />
            <KeyboardArrowDownIcon className={classes.statusIcon} />
          </Button>
          <Menu
            keepMounted
            id="statuses-menu"
            anchorEl={anchorEl?.status}
            open={Boolean(anchorEl?.status)}
            onClose={() => handleClose('status')}
          >
            {TaskConstants.statuses?.map((status, index) => {
              // eslint-disable-next-line array-callback-return
              if (status.filter === 'all') return;
              return (
                <MenuItem
                  key={index}
                  onClick={() => handleClose('status', { status: status.value })}
                >
                  {i18n.t(`tasks_statuses.${status.value}`)}
                </MenuItem>
              );
            })}
          </Menu>
        </>
      )}
    </Box>
  );

  /**
   * Vertical menu list
   * It has the label and the function for each action
   */
  const items = [
    {
      title: i18n.t('tasks.edit'),
      can: { action: 'update', name: 'task' },
      onClick: handleOpenTaskModal,
    },
  ];

  useEffect(() => {
    if (id) {
      getTaskById(id);
    }
    return () => {
      dispatch(TasksActions.resetCurrentTask());
    };
  }, []); // eslint-disable-line

  const NameComponent = ({ user }) => {
    return user?.first_name && user?.last_name ? `${user?.first_name} ${user?.last_name}` : ' - ';
  };

  const NameIconComponent = ({ user }) => {
    return user?.first_name && user?.last_name
      ? `${user?.first_name?.charAt(0)}${user?.last_name?.charAt(0)}`
      : '';
  };

  return (
    <Card
      menuItems={items}
      data-cy="task-card"
      title={renderCardHeader}
      headerActionType="vertical"
      contentRootClasses={cardClasses}
    >
      <Box className={classes.container}>
        {loading ? (
          <Box
            className={classes.loadingContainer}
            justifyContent="center"
            alignItems="center"
            display="flex"
          >
            <Loading />
          </Box>
        ) : (
          <>
            <Box className={classes.cardCustomPadding}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h3">
                  <Box fontSize={16} fontWeight="fontWeightMedium">
                    {current?.display_name || '--'}
                  </Box>
                </Typography>
                {current.priority && (
                  <>
                    <Button
                      aria-haspopup="true"
                      aria-controls="priority-menu"
                      className={classes.menuButton}
                      onClick={(e) => handleClick(e, 'priority')}
                    >
                      <Priority priority={current.priority} gutterBottom={false} />
                      <KeyboardArrowDownIcon className={classes.prioritiesIcon} />
                    </Button>
                    <Menu
                      id="priority-menu-items"
                      keepMounted={false}
                      anchorEl={anchorEl?.priority}
                      open={Boolean(anchorEl?.priority)}
                      onClose={() => handleClose('priority')}
                    >
                      {priorities.map((priority, index) => (
                        <MenuItem
                          key={index}
                          onClick={() => handleClose('priority', { priority: priority.value })}
                        >
                          {i18n.t(`priorities.${priority.value}`)}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}
              </Box>
            </Box>
            <Box className={classes.cardContentPadding}>
              <Box>
                <InfoGrid
                  classes={classes}
                  startIcon="avatar"
                  avatarClassName="avatarOwner"
                  startIconText={<NameIconComponent user={current?.owner_user} />}
                  title={`${i18n.t('tasks.assigned_to')}:`}
                  description={<NameComponent user={current?.owner_user} />}
                />
                <InfoGrid
                  classes={classes}
                  startIcon="avatar"
                  avatarClassName="avatarCreator"
                  startIconText={<NameIconComponent user={current?.creator} />}
                  title={`${i18n.t('tasks.creator')}:`}
                  description={<NameComponent user={current?.creator} />}
                />
                {current?.opened_time && (
                  <InfoGrid
                    classes={classes}
                    startIcon={TodayIcon}
                    title={`${i18n.t('tasks.open_since')}:`}
                    description={forceTimezone(moment(current?.opened_time)).format(
                      `${localeFormat} h:mm A`
                    )}
                  />
                )}
                {current?.due_time && (
                  <InfoGrid
                    classes={classes}
                    startIcon={TodayIcon}
                    title={`${i18n.t('common.due_date')}:`}
                    description={forceTimezone(moment(current?.due_time)).format(
                      `${localeFormat} h:mm A`
                    )}
                  />
                )}
                <InfoGrid
                  classes={classes}
                  title={`${i18n.t('common.description')}:`}
                  description={current?.description || '-'}
                />
              </Box>
              {current?.relationships?.length > 0 && (
                <Box display="flex" mt={4}>
                  <Typography variant="h4">
                    <Box
                      mr={2}
                      fontSize={13}
                      fontWeight="fontWeightBold"
                      className={classes.textSecondary}
                    >
                      {i18n.t('tasks.tags')}:
                    </Box>
                  </Typography>
                  <Box display="flex" alignItems="center" flexWrap="wrap">
                    {current?.relationships?.map((tag, index) => (
                      <Tag
                        key={index}
                        item={getDynamicLink(`${tag?.class} ID#${tag?.id}`, tag?.id, tag.class)}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
      <Suspense fallback={<Loading />}>
        <TaskModal
          current={current}
          open={openTaskModal}
          id={current?.task_source_id}
          type={current?.task_source_name}
          handleClick={handleOpenTaskModal}
        />
      </Suspense>
    </Card>
  );
};

TaskWidget.propTypes = {
  id: PropTypes.string.isRequired,
};

export default TaskWidget;
